import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  Button,
  Table,
  StatusIndicator,
  Spinner,
  BreadcrumbGroup,
  Alert,
  ColumnLayout,
  Modal,
  Select,
  FileUpload,
  ProgressBar,
  Icon,
  Pagination,
} from '@cloudscape-design/components';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { useNotifications } from './NotificationContext';
import AppLayoutWrapper from './AppLayoutWrapper';

const effectiveKbId = (kb) => kb.id || kb.adexcloud_kb_id;

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  const num = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;
  if (Number.isNaN(num)) return String(bytes);
  if (num === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(num) / Math.log(k));
  return `${(num / k ** i).toFixed(i > 0 ? 2 : 0)} ${sizes[i]}`;
};

const fileStatusType = (status) => {
  const s = String(status || '').toLowerCase();
  if (['success', 'completed', 'ready'].includes(s)) return 'success';
  if (['failed', 'error'].includes(s)) return 'error';
  return 'pending';
};

export default function KnowledgeBaseFiles() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { error: notifyError, success: notifySuccess } = useNotifications();
  const accessToken = user?.access_token || user?.token;

  const [kbs, setKbs] = useState([]);
  const [kbsLoading, setKbsLoading] = useState(true);
  const [selectedKb, setSelectedKb] = useState(null);

  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState(null);

  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [pendingUploadFiles, setPendingUploadFiles] = useState([]);
  const [fileDetails, setFileDetails] = useState({});

  const [selectedFile, setSelectedFile] = useState(null);
  const [vectorOnly, setVectorOnly] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  const [quota, setQuota] = useState({ total: 0, used: 0, remaining: 0, loading: true });

  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const pageSize = 10;

  const fetchQuota = useCallback(async () => {
    if (!accessToken) return;
    try {
      const resp = await axios.get('/api/billing/quota/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const storage = resp.data?.data?.resources?.document_storage_mb || { total: 0, used: 0, remaining: 0 };
      setQuota({ total: storage.total, used: storage.used, remaining: storage.remaining, loading: false });
    } catch (err) {
      console.error('Quota fetch error:', err);
      setQuota((prev) => ({ ...prev, loading: false }));
    }
  }, [accessToken]);

  const fetchKBs = useCallback(async () => {
    if (!accessToken) return;
    setKbsLoading(true);
    try {
      const response = await axios.get('/api/knowledge-bases/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const raw = response.data?.data || response.data || [];
      const list = Array.isArray(raw) ? raw : [];
      setKbs(list);
    } catch (err) {
      console.error('KB fetch error:', err);
      notifyError('Unable to load knowledge bases.');
    } finally {
      setKbsLoading(false);
    }
  }, [accessToken, notifyError]);

  const fetchFiles = useCallback(async (kbId) => {
    if (!kbId || !accessToken) return;
    setFilesLoading(true);
    setFilesError(null);
    try {
      const res = await axios.get(`/api/knowledge-bases/${kbId}/files/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = res.data?.data;
      let raw;
      if (Array.isArray(data)) {
        raw = data;
      } else if (data && typeof data === 'object') {
        raw = data.files || data.items || data.documents || [];
      } else {
        raw = [];
      }
      setFiles(Array.isArray(raw) ? raw : []);
    } catch (err) {
      console.error('File fetch error:', err);
      setFilesError('Unable to load files for this knowledge base.');
      setFiles([]);
    } finally {
      setFilesLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (user) {
      fetchKBs();
      fetchQuota();
    }
  }, [user, fetchKBs, fetchQuota]);

  useEffect(() => {
    if (selectedKb) {
      const kbId = effectiveKbId(selectedKb);
      fetchFiles(kbId);
      fetchQuota();
    } else {
      setFiles([]);
    }
  }, [selectedKb, fetchFiles, fetchQuota]);

  const kbOptions = useMemo(
    () => kbs.map((kb) => ({ label: kb.name || 'Unnamed KB', value: effectiveKbId(kb), kb })),
    [kbs]
  );

  const handleKbSelect = (option) => {
    setSelectedKb(option?.kb || null);
    setCurrentPageIndex(1);
  };

  const handleFileUpload = async (filesToUpload) => {
    if (!selectedKb || !filesToUpload.length) return;
    const kbId = effectiveKbId(selectedKb);
    if (!kbId) {
      notifyError('Cannot upload: KB has no valid ID');
      return;
    }
    for (const file of filesToUpload) {
      const tempId = `${file.name}-${Date.now()}`;
      setUploadQueue((prev) => [...prev, { id: tempId, name: file.name, status: 'uploading' }]);
      setUploadProgress((prev) => ({ ...prev, [tempId]: 0 }));
      try {
        const formData = new FormData();
        formData.append('file', file);
        const resp = await axios.post(`/api/knowledge-bases/${kbId}/upload/`, formData, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const pct = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            setUploadProgress((prev) => ({ ...prev, [tempId]: pct }));
          },
        });
        const serverFileId = resp.data?.data?.file_id;
        if (serverFileId) {
          setUploadQueue((prev) =>
            prev.map((f) => (f.id === tempId ? { ...f, serverFileId, status: 'pending' } : f))
          );
        } else {
          setUploadQueue((prev) =>
            prev.map((f) => (f.id === tempId ? { ...f, status: 'success' } : f))
          );
          fetchFiles(kbId);
          fetchQuota();
        }
      } catch (err) {
        notifyError('Upload failed: ' + (err.response?.data?.message || err.message));
        setUploadQueue((prev) =>
          prev.map((f) => (f.id === tempId ? { ...f, status: 'failed' } : f))
        );
      }
    }
  };

  // Poll upload status for pending files
  useEffect(() => {
    if (!selectedKb) return;
    const pendingFiles = uploadQueue.filter((f) => f.status === 'pending' && f.serverFileId);
    if (!pendingFiles.length) return;

    const kbId = effectiveKbId(selectedKb);
    if (!kbId) return;

    const interval = setInterval(async () => {
      for (const f of pendingFiles) {
        try {
          const resp = await axios.get(
            `/api/knowledge-bases/${kbId}/files/${f.serverFileId}/status/`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          const data = resp.data?.data;
          const st = data?.status;
          // Store detailed file info for display
          setFileDetails((prev) => ({
            ...prev,
            [f.id]: {
              status: st,
              processing: data?.processing,
              cost_analysis: data?.cost_analysis,
              adexcloud_status: data?.adexcloud_status,
              updated_at: data?.updated_at,
            },
          }));
          if (['success', 'completed', 'failed'].includes(st)) {
            setUploadQueue((prev) =>
              prev.map((item) => (item.id === f.id ? { ...item, status: st } : item))
            );
            if (['success', 'completed'].includes(st)) {
              fetchFiles(kbId);
              fetchQuota();
            }
          }
        } catch (err) {
          console.error('Status poll error:', err);
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [uploadQueue, selectedKb, accessToken, fetchFiles, fetchQuota]);

  const handleDeleteFile = async () => {
    if (!selectedKb || !selectedFile) return;
    const kbId = effectiveKbId(selectedKb);
    const fileId = selectedFile.id || selectedFile.file_id;
    if (!kbId || !fileId) {
      notifyError('Cannot delete: missing KB or file ID');
      return;
    }
    try {
      if (vectorOnly) {
        await axios.delete(
          `/api/knowledge-bases/${kbId}/vectordb-files/${fileId}/delete/`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      } else {
        await axios.delete(
          `/api/knowledge-bases/${kbId}/files/${fileId}/delete/`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      }
      notifySuccess(vectorOnly ? 'File removed from vector DB' : 'File deleted');
      setFiles((prev) => prev.filter((f) => (f.id || f.file_id) !== fileId));
      setDeleteModalVisible(false);
      fetchQuota();
    } catch (err) {
      notifyError('Delete file failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const openDelete = (item, vectorOnlyFlag) => {
    setSelectedFile(item);
    setVectorOnly(!!vectorOnlyFlag);
    setDeleteModalVisible(true);
  };

  const allFiles = useMemo(() => {
    const queueItems = uploadQueue.map((f) => ({
      id: f.id,
      name: f.name,
      status: f.status,
      size: null,
      source: 'uploading',
      kbName: selectedKb?.name || 'Uploading',
      isQueue: true,
      details: fileDetails[f.id] || null,
      progress: uploadProgress[f.id] || 0,
    }));
    const serverItems = files.map((f) => ({
      id: f.id || f.file_id || f.document_id || f.name,
      name: f.name || f.filename || f.file_name || f.original_name || f.document_name || 'Unnamed',
      status: f.status || f.file_status || f.processing_status || 'ready',
      size: f.size || f.file_size || f.bytes,
      source: f.source || 'adexcloud',
      kbName: selectedKb?.name || 'KB',
      isQueue: false,
      raw: f,
    }));
    return [...queueItems, ...serverItems];
  }, [files, uploadQueue, selectedKb, fileDetails, uploadProgress]);

  const visibleFiles = useMemo(() => {
    const start = (currentPageIndex - 1) * pageSize;
    return allFiles.slice(start, start + pageSize);
  }, [allFiles, currentPageIndex, pageSize]);

  return (
    <AppLayoutWrapper
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: 'Home', href: '/dashboard' },
            { text: 'Knowledge Bases', href: '/knowledge-bases' },
            { text: 'Files', href: '/knowledge-base-files' },
          ]}
          onFollow={(event) => {
            event.preventDefault();
            navigate(event.detail.href);
          }}
        />
      }
      header={{
        title: 'Knowledge Base Files',
        description: 'Manage uploaded documents, check ingestion status, and control storage quotas.',
      }}
      headerBackgroundStyle="linear-gradient(to bottom, rgba(9, 29, 43, 1) 2%, rgba(0, 108, 224, 1) 8%, rgba(255, 255, 255, 1) 82%)"
      helpPanelHeader="KB Files"
      helpPanelContent={
        <div>
          <p>Files uploaded to knowledge bases are stored in S3 and vectorized for agent retrieval.</p>
          <ul>
            <li>Delete from S3 + Vector DB removes the file completely.</li>
            <li>Delete from Vector DB only keeps the original file but removes it from search.</li>
            <li>Storage quota is shared across all knowledge bases in your company.</li>
          </ul>
        </div>
      }
    >
      <SpaceBetween size="l">
        {/* Quota summary */}
        <ColumnLayout columns={{ default: 1, s: 2, m: 3, l: 3, xl: 3 }} variant="text-grid">
          <Container>
            <Box variant="awsui-key-label"><Icon name="file" variant="normal" /> Storage quota</Box>
            <Box variant="h1">{quota.loading ? <Spinner size="normal" /> : `${quota.total} MB`}</Box>
          </Container>
          <Container>
            <Box variant="awsui-key-label"><Icon name="upload-download" variant="normal" /> Used</Box>
            <Box variant="h1" color={quota.used >= quota.total ? 'text-status-error' : 'text-status-success'}>
              {quota.loading ? <Spinner size="normal" /> : `${quota.used.toFixed(2)} MB`}
            </Box>
          </Container>
          <Container>
            <Box variant="awsui-key-label"><Icon name="status-positive" variant="normal" /> Remaining</Box>
            <Box variant="h1">{quota.loading ? <Spinner size="normal" /> : `${Math.max(0, quota.remaining).toFixed(2)} MB`}</Box>
          </Container>
        </ColumnLayout>

        {quota.remaining <= 0 && !quota.loading && (
          <Alert type="warning" header="Storage quota exceeded">
            You have used {quota.used.toFixed(2)} / {quota.total} MB. Delete files or purchase more storage to upload new documents.
          </Alert>
        )}

        {/* KB selector and upload */}
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <Button variant="primary" iconName="add-plus" onClick={() => navigate('/knowledge-base/new')}>
                  Add Knowledge Base
                </Button>
              }
            >
              Select Knowledge Base
            </Header>
          }
        >
          <SpaceBetween size="m">
            <Select
              placeholder={kbsLoading ? 'Loading knowledge bases...' : 'Choose a knowledge base'}
              selectedOption={selectedKb ? { label: selectedKb.name || 'Unnamed KB', value: effectiveKbId(selectedKb) } : null}
              onChange={({ detail }) => handleKbSelect(detail.selectedOption)}
              options={kbOptions}
              loading={kbsLoading}
              empty="No knowledge bases found"
            />

            {selectedKb && (
              <FileUpload
                value={pendingUploadFiles}
                onChange={({ detail }) => {
                  setPendingUploadFiles(detail.value);
                  if (detail.value.length > 0) {
                    handleFileUpload(detail.value);
                    setPendingUploadFiles([]);
                  }
                }}
                constraintText="Supported formats: .pdf, .txt, .docx (Max 10MB). Storage quota must be available."
                i18nStrings={{
                  uploadButtonText: () => 'Upload File',
                  dropzoneText: () => 'Drop file here',
                  removeFileAriaLabel: (e) => `Remove ${e}`,
                  errorIconAriaLabel: 'Error',
                }}
                disabled={quota.remaining <= 0}
              />
            )}
          </SpaceBetween>
        </Container>

        {/* Files table */}
        {selectedKb && (
          <Container
            header={
              <Header variant="h2" counter={`(${allFiles.length})`}>
                Files in {selectedKb.name || 'KB'}
              </Header>
            }
          >
            {filesLoading ? (
              <Box textAlign="center" padding="l"><Spinner size="large" /></Box>
            ) : filesError ? (
              <Alert type="error" header="Error loading files">{filesError}</Alert>
            ) : (
              <Table
                columnDefinitions={[
                  {
                    id: 'name',
                    header: 'Name',
                    cell: (item) => item.name,
                    width: 350,
                  },
                  {
                    id: 'size',
                    header: 'Size',
                    cell: (item) => (item.size !== null && item.size !== undefined ? formatBytes(item.size) : '—'),
                    width: 120,
                  },
                  {
                    id: 'status',
                    header: 'Status',
                    cell: (item) => {
                      if (item.isQueue) {
                        if (item.status === 'uploading') {
                          return (
                            <div style={{ minWidth: '120px' }}>
                              <ProgressBar value={item.progress} label="Uploading..." description={`${item.progress}%`} />
                            </div>
                          );
                        }
                        return <StatusIndicator type={fileStatusType(item.status)}>{item.status}</StatusIndicator>;
                      }
                      return <StatusIndicator type={fileStatusType(item.status)}>{item.status}</StatusIndicator>;
                    },
                    width: 180,
                  },
                  {
                    id: 'details',
                    header: 'Processing Details',
                    cell: (item) => {
                      if (!item.isQueue || !item.details) return '—';
                      const d = item.details;
                      const proc = d.processing || (d.adexcloud_status && d.adexcloud_status.processing);
                      const steps = [];
                      if (proc) {
                        if (proc.downloading !== undefined) steps.push({ label: 'Download', done: proc.downloading === true || proc.downloading === 'done' });
                        if (proc.extracting !== undefined) steps.push({ label: 'Extract', done: proc.extracting === true || proc.extracting === 'done' });
                        if (proc.chunking !== undefined) steps.push({ label: 'Chunk', done: proc.chunking === true || proc.chunking === 'done' });
                        if (proc.embedding !== undefined) steps.push({ label: 'Embed', done: proc.embedding === true || proc.embedding === 'done' });
                        if (proc.indexing !== undefined) steps.push({ label: 'Index', done: proc.indexing === true || proc.indexing === 'done' });
                        if (proc.uploading !== undefined) steps.push({ label: 'Upload', done: proc.uploading === true || proc.uploading === 'done' });
                      }
                      const cost = d.cost_analysis;
                      return (
                        <SpaceBetween size="xs">
                          {steps.length > 0 && (
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {steps.map((s) => (
                                <StatusIndicator key={s.label} type={s.done ? 'success' : 'in-progress'}>
                                  {s.label}
                                </StatusIndicator>
                              ))}
                            </div>
                          )}
                          {proc?.error && (
                            <Box variant="small" color="text-status-error">{proc.error}</Box>
                          )}
                          {cost && (
                            <Box variant="small" color="text-body-secondary">
                              {cost.tokens ? `${cost.tokens} tokens` : ''}
                              {cost.tokens && cost.cost ? ' · ' : ''}
                              {cost.cost ? `${cost.cost} ${cost.currency || ''}` : ''}
                            </Box>
                          )}
                          {!steps.length && !cost && !proc?.error && (
                            <StatusIndicator type="in-progress">Processing on adexcloud...</StatusIndicator>
                          )}
                        </SpaceBetween>
                      );
                    },
                    width: 280,
                  },
                  {
                    id: 'source',
                    header: 'Source',
                    cell: (item) => (item.isQueue ? <StatusIndicator type="info">Local upload</StatusIndicator> : 'adexcloud'),
                    width: 140,
                  },
                  {
                    id: 'actions',
                    header: 'Actions',
                    cell: (item) => {
                      if (item.isQueue) return null;
                      return (
                        <SpaceBetween direction="horizontal" size="xs">
                          <Button
                            iconName="remove"
                            variant="icon"
                            onClick={() => openDelete(item.raw, false)}
                            title="Delete from S3 + Vector DB"
                          />
                          <Button
                            iconName="close"
                            variant="icon"
                            onClick={() => openDelete(item.raw, true)}
                            title="Delete from Vector DB only"
                          />
                        </SpaceBetween>
                      );
                    },
                    width: 140,
                  },
                ]}
                items={visibleFiles}
                pagination={
                  <Pagination
                    currentPageIndex={currentPageIndex}
                    pagesCount={Math.max(1, Math.ceil(allFiles.length / pageSize))}
                    onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
                    openEnd={false}
                  />
                }
                empty={
                  <Box textAlign="center" padding="l">
                    <SpaceBetween size="m">
                      <b>No files found</b>
                      <p>Upload files to this knowledge base.</p>
                    </SpaceBetween>
                  </Box>
                }
              />
            )}
          </Container>
        )}
      </SpaceBetween>

      {/* File Delete Modal */}
      {deleteModalVisible && (
        <Modal
          visible={deleteModalVisible}
          onDismiss={() => setDeleteModalVisible(false)}
          header={vectorOnly ? 'Delete from Vector DB Only' : 'Delete File (S3 + Vector DB)'}
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setDeleteModalVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleDeleteFile}>Delete</Button>
            </SpaceBetween>
          }
        >
          Are you sure you want to delete <b>{selectedFile?.name || selectedFile?.filename || selectedFile?.file_name || selectedFile?.original_name}</b>?
          {vectorOnly ? (
            <p>This will only remove the file from the vector database, keeping the original in S3.</p>
          ) : (
            <p>This will permanently delete the file from both S3 and the vector database.</p>
          )}
        </Modal>
      )}
    </AppLayoutWrapper>
  );
}
